import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { Plus, Pencil, X } from "lucide-react";
import { toast, Button, Input, Label, Textarea } from "@ndd/ui";
import { cn } from "@/lib/utils";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  OBSERVATION_IMAGE_MAX_COUNT,
  OBSERVATION_TAGS,
  type ObservationTag,
  type ObservationNoteData,
  type ObservationImageData,
} from "../../../../shared/observation-types";
import { todayIsoDate } from "../../../../shared/local-date";
import {
  uploadObservationImages,
  validateObservationImageFile,
} from "@/lib/observation-image-client";
import {
  ObservationImagePicker,
  type PendingObservationImage,
} from "./observation-image-picker";
import { ObservationImageGallery } from "./observation-image-gallery";
import { TAG_COLOR, TAG_LABELS } from "./observation-tag";

interface ObservationFormProps {
  rdId: string;
  /** When provided, the form edits this note; otherwise it creates a new one. */
  note?: ObservationNoteData;
  onSuccess: () => void;
  onImagesChanged?: () => void;
  onClose: () => void;
}

const FIELD_BASE =
  "h-9 rounded-[10px] bg-sand-50 border-sand-200 text-foreground text-[13px] md:text-[13px] px-3 placeholder:text-sand-400 shadow-none";

const LABEL_BASE =
  "text-[11px] font-semibold uppercase tracking-wider text-sand-500";

export function ObservationForm({
  rdId,
  note,
  onSuccess,
  onImagesChanged,
  onClose,
}: ObservationFormProps) {
  const { getToken } = useAuth();
  const isEdit = note !== undefined;
  // An observation can't have happened in the future, so cap the date at today.
  const today = todayIsoDate();
  const [tag, setTag] = useState<ObservationTag | null>(note?.tag ?? null);
  const [title, setTitle] = useState(note?.title ?? "");
  const [observationDate, setObservationDate] = useState(
    note?.observationDate ?? today,
  );
  const [body, setBody] = useState(note?.body ?? "");
  const [imageOverride, setImageOverride] = useState<ObservationImageData[] | null>(null);
  const existingImages = imageOverride ?? note?.images ?? [];
  const [pendingImages, setPendingImages] = useState<PendingObservationImage[]>(
    [],
  );
  const [persistedNoteId, setPersistedNoteId] = useState<string | null>(
    note?.id ?? null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [tagInvalid, setTagInvalid] = useState(false);
  const pendingImagesRef = useRef<PendingObservationImage[]>([]);

  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    return () => {
      for (const image of pendingImagesRef.current) {
        URL.revokeObjectURL(image.previewUrl);
      }
    };
  }, []);

  function clearPendingImages() {
    for (const image of pendingImagesRef.current) {
      URL.revokeObjectURL(image.previewUrl);
    }
    pendingImagesRef.current = [];
    setPendingImages([]);
  }

  function handleAddImages(files: File[]) {
    const remainingSlots =
      OBSERVATION_IMAGE_MAX_COUNT - existingImages.length - pendingImages.length;
    if (remainingSlots <= 0) {
      setImageError("Observations can have at most 10 images.");
      return;
    }

    const accepted: PendingObservationImage[] = [];
    let nextError: string | null = null;
    for (const file of files) {
      if (accepted.length >= remainingSlots) {
        nextError = "Observations can have at most 10 images.";
        break;
      }

      const validationError = validateObservationImageFile(file);
      if (validationError) {
        nextError = validationError;
        continue;
      }

      accepted.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (accepted.length > 0) {
      setPendingImages((prev) => [...prev, ...accepted]);
    }
    setImageError(nextError);
  }

  function handleRemovePendingImage(id: string) {
    setPendingImages((prev) => {
      const image = prev.find((item) => item.id === id);
      if (image) URL.revokeObjectURL(image.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
    setImageError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;

    if (!tag) {
      setTagInvalid(true);
      return;
    }

    const trimmedBody = body.trim();
    if (!trimmedBody) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let targetNoteId = persistedNoteId;
      const endpoint = targetNoteId
        ? `/api/rds/${rdId}/observations/${targetNoteId}`
        : `/api/rds/${rdId}/observations`;

      let saved: { data?: { id?: string } };
      try {
        saved = await authenticatedApi<{ data?: { id?: string } }>(getToken, endpoint, {
          method: targetNoteId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tag,
            title: title.trim() || null,
            body: trimmedBody,
            observationDate,
          }),
        });
      } catch (saveError) {
        if (saveError instanceof ApiRequestError) {
          const errBody = saveError.body as { error?: unknown } | undefined;
          setError(
            typeof errBody?.error === "string"
              ? errBody.error
              : "Failed to save observation.",
          );
          return;
        }
        throw saveError;
      }

      if (!targetNoteId) {
        targetNoteId = saved.data?.id ?? null;
        if (!targetNoteId) {
          setError("Observation saved, but image upload could not start.");
          return;
        }
        setPersistedNoteId(targetNoteId);
      }

      if (pendingImages.length > 0) {
        try {
          await uploadObservationImages({
            rdId,
            noteId: targetNoteId,
            files: pendingImages.map((image) => image.file),
          });
          clearPendingImages();
        } catch (uploadError) {
          Sentry.captureException(uploadError, {
            tags: {
              source: "client",
              surface: "observations",
              operation: "upload_observation_images_after_save",
            },
          });
          toast.error("Observation saved, but images did not upload");
          return;
        }
      }

      onSuccess();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-[14px] rounded-2xl border border-sand-200 bg-card px-5 py-[18px] shadow-[0_10px_24px_rgba(40,3,15,0.08)]"
    >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-[10px]">
              <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-interactive-light">
                {isEdit ? (
                  <Pencil className="h-[13px] w-[13px] text-interactive" strokeWidth={2.5} />
                ) : (
                  <Plus className="h-[14px] w-[14px] text-interactive" strokeWidth={2.5} />
                )}
              </div>
              <h3 className="shrink-0 font-sans text-[15px] font-semibold leading-[18px] text-foreground">
                {isEdit ? "Edit observation" : "Add observation"}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-sand-100 text-sand-500 transition-colors hover:text-foreground"
            >
              <X className="h-[14px] w-[14px]" />
            </button>
          </div>

          {/* Tag */}
          <div className="flex flex-col gap-1">
            <Label className={LABEL_BASE}>Tag</Label>
            <div className="flex flex-wrap gap-2">
              {OBSERVATION_TAGS.map((t) => {
                const selected = tag === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setTag(t);
                      setTagInvalid(false);
                    }}
                    className={cn(
                      "rounded-full border px-[13px] py-[6px] font-sans text-[12px] font-semibold transition-colors",
                      selected
                        ? cn(TAG_COLOR[t].fill, TAG_COLOR[t].border)
                        : "border-sand-200 bg-card text-sand-500 hover:bg-sand-50",
                      tagInvalid && "ring-2 ring-error",
                    )}
                  >
                    {TAG_LABELS[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title + observation date — 2:1 ratio */}
          <div className="flex gap-3">
            <div className="flex min-w-0 flex-[2] flex-col gap-1">
              <Label htmlFor="observation-form-title" className={LABEL_BASE}>
                Title (optional)
              </Label>
              <Input
                id="observation-form-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Cancelled check-in again"
                disabled={isSubmitting}
                className={FIELD_BASE}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Label htmlFor="observation-form-date" className={LABEL_BASE}>
                Observation date
              </Label>
              <Input
                id="observation-form-date"
                type="date"
                value={observationDate}
                max={today}
                onChange={(e) => setObservationDate(e.target.value)}
                disabled={isSubmitting}
                className={FIELD_BASE}
              />
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-1">
            <Label htmlFor="observation-form-body" className={LABEL_BASE}>
              Observation
            </Label>
            <Textarea
              id="observation-form-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What did you notice?"
              disabled={isSubmitting}
              className="h-[110px] min-h-[110px] resize-none rounded-[10px] border-sand-200 bg-sand-50 px-[14px] py-3 text-[13px] text-sand-600 shadow-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className={LABEL_BASE}>Images (optional)</Label>
            {persistedNoteId && existingImages.length > 0 && (
              <ObservationImageGallery
                rdId={rdId}
                noteId={persistedNoteId}
                images={existingImages}
                editable
                heading="Attached"
                onChanged={(images) => {
                  setImageOverride(images);
                  onImagesChanged?.();
                }}
              />
            )}
            <ObservationImagePicker
              files={pendingImages}
              existingCount={existingImages.length}
              disabled={isSubmitting}
              error={imageError}
              onAddFiles={handleAddImages}
              onRemoveFile={handleRemovePendingImage}
            />
          </div>

          {error && <p className="text-sm text-error">{error}</p>}

          {/* Footer */}
          <div className="flex items-center justify-end gap-[10px]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-9 rounded-[10px] border-sand-200 px-4 text-[13px] font-medium"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!body.trim() || isSubmitting}
              className="h-9 rounded-[10px] bg-interactive px-[18px] text-[13px] font-medium text-white shadow-sm hover:bg-interactive-hover"
            >
              {isSubmitting
                ? "Saving..."
                : isEdit
                  ? "Save changes"
                  : "Save observation"}
            </Button>
          </div>
    </form>
  );
}
